import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ONE_SHOOT_OUTPUTS } from "@/content/site";
import { useMedia } from "@/context/MediaContext";
import { MediaFrame } from "@/components/media/MediaFrame";
import { Eyebrow, Reveal } from "@/components/site/Reveal";

// The media is the hero. Outputs read like a running editorial list, not deliverables.
const OneShoot = () => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1]);
  const { items } = useMedia("oneshoot", 1);

  return (
    <section ref={ref} id="one-shoot" className="py-32 md:py-48 bg-[#F2EFE9] overflow-hidden" data-testid="one-shoot" aria-labelledby="one-shoot-heading">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow>One shoot</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 id="one-shoot-heading" className="mt-7 font-display uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95] text-[#2E1B16]">
                One shoot should do more than fill your Instagram.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-lg md:text-xl text-[#2E1B16]/65 leading-relaxed max-w-md">
                We don't film content just to keep a feed active. We capture enough from a business to create assets that can work across the entire customer journey.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <ul className="mt-12 columns-2 gap-x-8 text-[#2E1B16]" data-testid="one-shoot-outputs">
                {ONE_SHOOT_OUTPUTS.map((o, i) => (
                  <li key={o} className="break-inside-avoid border-t border-[#2E1B16]/12 py-3 flex items-baseline gap-3">
                    <span className="text-[0.65rem] font-semibold text-[#38BDF8] tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                    <span className={`text-lg ${i % 4 === 1 ? "font-accent text-2xl" : "font-medium"}`}>{o}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-7 relative">
            <div className="relative overflow-hidden rounded-sm">
              <motion.div style={{ scale: reduced ? 1 : scale }}>
                <MediaFrame item={items[0]} ratio="aspect-[4/5] lg:aspect-[16/17]" autoplay hoverPlay={false} sizes="(min-width:1024px) 58vw, 100vw" />
              </motion.div>
            </div>
            <Reveal delay={0.1}>
              <p className="relative z-10 mt-8 lg:mt-10 lg:-ml-16 font-display uppercase text-[2.6rem] sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight text-[#2E1B16]" data-testid="one-shoot-tagline">
                Shot once.<br />
                <span className="font-accent font-normal normal-case text-[#38BDF8] text-[1.1em]">Used everywhere.</span>
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OneShoot;
